import { type PropsWithChildren, memo } from "react";

import { type Maybe } from "@shopify/hydrogen-react/storefront-api-types";

import { CompareAtPriceDisplay } from "@components/Price/constants";
import { Text, type TextType } from "@react/components/Text";
import { type WithBreakpoints } from "@react/utilities";
import { tw, type TTailwindString } from "classnames";

type SizeValue = "3xl" | "lg" | "md" | "sm" | "xs" | "2xs" | "3xs";

type Props = {
  as?: TextType;
  className?: TTailwindString;
  compareAtPrice?: string;
  compareAtPriceDisplay?: Maybe<CompareAtPriceDisplay>;
  compareAtPriceSize?: SizeValue | WithBreakpoints<SizeValue>;
  isFree: boolean;
  price: string;
  showsBundleValueDisclaimer: boolean;
  size?: SizeValue | WithBreakpoints<SizeValue>;
};

const styles = {
  container: (className: Props["className"]) =>
    tw("inline-flex", className ? tw(className) : undefined),
  price: (highlighted: boolean) =>
    tw({
      "text-background-primary-inverse": !highlighted,
      "text-special": highlighted,
    }),
  compareAtPrice: (compareAtPriceDisplay?: Props["compareAtPriceDisplay"]) =>
    tw("ml-2", "text-secondary", {
      "line-through": compareAtPriceDisplay !== CompareAtPriceDisplay.VALUE,
    }),
};

const PriceViewBase = ({
  as,
  className,
  compareAtPrice,
  compareAtPriceDisplay,
  compareAtPriceSize,
  isFree,
  price,
  showsBundleValueDisclaimer,
  size,
}: PropsWithChildren<Props>) => {
  return (
    <span className={styles.container(className)}>
      <Text
        as={as}
        className={styles.price(!!compareAtPrice || isFree)}
        size={size}
      >
        <span className="price">{price}</span>
      </Text>
      {!!compareAtPrice && (
        <Text
          as={as}
          className={styles.compareAtPrice(compareAtPriceDisplay)}
          size={compareAtPriceSize ?? size}
        >
          <span className="compare-at-price">{compareAtPrice}</span>
          {showsBundleValueDisclaimer && "†"}
        </Text>
      )}
    </span>
  );
};

export const PriceView = memo(PriceViewBase);
